import {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, ControllerProps, useFormContext } from 'react-hook-form';

import {
  getMallTitlesMap,
  MALL_OPTION_NONE,
  MALL_OPTION_OTHER,
} from '~domains/partner-application/entities/application';
import { Mall, useMalls } from '~domains/partner-application/entities/mall';

import { Combobox } from '@breezeventures/ui-kit/combobox/rhf';
import { Typography } from '@breezeventures/ui-kit/typography';

type SelectMallProps = Omit<ControllerProps, 'render' | 'name' | 'control'> & {
  name: string;
  label?: string;
  provinceFieldName?: string;
  control?: ControllerProps['control'];
};

export const SelectMall = ({
  name,
  label = 'What mall is your store located in?',
  provinceFieldName = 'province',
  ...controllerProps
}: SelectMallProps) => {
  const formContext = useFormContext();
  const selectedProvince = formContext.watch(provinceFieldName) ?? '';

  const selectMalls = useCallback(
    (malls: Mall[]) => {
      const filteredMalls = malls.filter(
        (m) => !m.address?.province || m.address.province === selectedProvince
      );

      return [MALL_OPTION_NONE, MALL_OPTION_OTHER, ...filteredMalls];
    },
    [selectedProvince]
  );

  const {
    data: malls = [],
    isLoading,
    isError,
  } = useMalls({
    select: selectMalls,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const previousProvinceRef = useRef<string>(selectedProvince);

  useEffect(() => {
    if (previousProvinceRef.current !== selectedProvince) {
      formContext.setValue(name, '');
      setSearchQuery('');
      previousProvinceRef.current = selectedProvince;
    }
  }, [selectedProvince, formContext, name]);

  const mallTitlesMap = useMemo(() => getMallTitlesMap(malls), [malls]);

  return (
    <Controller
      {...controllerProps}
      name={name}
      control={formContext?.control}
      render={(controllerRenderProps) => {
        const { field, ...controllerRest } = controllerRenderProps;
        return (
          <Combobox
            {...controllerRest}
            field={{
              ...field,
              onChange: (value: string) => {
                field.onChange(value);
                setSearchQuery('');
              },
            }}
            onClose={() => setSearchQuery('')}
            disabled={isLoading || isError}
            textFieldProps={{
              label,
              labelPosition: 'inner',
              onChange: (e) => setSearchQuery(e.target.value),
              displayValue: (mallId) => {
                if (searchQuery) return searchQuery;
                return mallId ? mallTitlesMap[mallId] || '' : '';
              },
            }}
          >
            {isError ? (
              <Combobox.Option value="" disabled>
                <Typography.Text>Failed to load malls</Typography.Text>
              </Combobox.Option>
            ) : (
              <SelectMallOptions
                malls={malls}
                searchQuery={deferredSearchQuery}
              />
            )}
          </Combobox>
        );
      }}
    />
  );
};

const SelectMallOptions = memo(function SelectMallOptions({
  malls,
  searchQuery,
}: {
  malls: Mall[];
  searchQuery: string;
}) {
  const filteredMalls = useMemo(() => {
    if (!searchQuery.trim()) {
      return malls;
    }
    const query = searchQuery.trim().toLowerCase();
    return malls.filter((mall) => mall.name.toLowerCase().includes(query));
  }, [malls, searchQuery]);

  if (filteredMalls.length === 0) {
    return (
      <Combobox.Option value="" disabled>
        <Typography.Text>No malls found</Typography.Text>
      </Combobox.Option>
    );
  }

  return filteredMalls.map((mall) => (
    <Combobox.Option key={mall.id} value={mall.id}>
      <Typography.Text>{mall.name}</Typography.Text>
    </Combobox.Option>
  ));
});
